import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/workflow";
import { uploadChunked } from "@/lib/chunkedUpload";
import { BannerLogo } from "@/components/banner/BannerItems";
import { BANNER_BG, clamp } from "@/lib/bannerLayout";
import { AlertTriangle, FileCheck2, Loader2, Printer, Upload, XCircle, AlignCenterHorizontal } from "lucide-react";

const de = (v) => String(Math.round(v * 10) / 10).replace(".", ",");
const num = (v) => parseFloat(String(v).replace(",", "."));

// Mirrors backend backdrop_box: logo box in mm, clamped into the safety area.
export function logoBox(d, lay) {
  const [W, H] = d.final_mm;
  const s = d.safety_mm;
  const w = clamp((lay.w_pct / 100) * W, 1, Math.max(1, Math.min(W - 2 * s, (H - 2 * s) * d.aspect)));
  const h = w / d.aspect;
  const cx = clamp((lay.x_pct / 100) * W, s + w / 2, Math.max(s + w / 2, W - s - w / 2));
  const cy = clamp((lay.y_pct / 100) * H, s + h / 2, Math.max(s + h / 2, H - s - h / 2));
  return { w, h, cx, cy };
}

function Canvas({ d, lay, setLay, onDone, onFile }) {
  const wrap = useRef(null);
  const [cw, setCw] = useState(600);
  const [snap, setSnap] = useState({});
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setCw(e.contentRect.width));
    if (wrap.current) ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);
  const [W, H] = d.final_mm;
  const h = Math.min(340, cw / (W / H));
  const width = h * (W / H);
  const k = width / W;
  const s = d.safety_mm;
  const b = d.aspect ? logoBox(d, lay) : null;
  const drag = (mode) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX; const sy = e.clientY; const start = lay; let cur = lay;
    const move = (ev) => {
      const dx = ((ev.clientX - sx) / width) * 100; const dy = ((ev.clientY - sy) / h) * 100;
      if (mode === "size") cur = { ...start, w_pct: clamp(start.w_pct + dx * 2, 1, 100) };
      else {
        let x = start.x_pct + dx; let y = start.y_pct + dy;
        const sxn = Math.abs(x - 50) < 1.5; const syn = Math.abs(y - 50) < 4;
        if (sxn) x = 50; if (syn) y = 50;
        setSnap({ x: sxn, y: syn });
        cur = { ...start, x_pct: clamp(x, 0, 100), y_pct: clamp(y, 0, 100) };
      }
      setLay(cur);
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); setSnap({}); onDone(cur); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  return (
    <div ref={wrap} className="w-full" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}>
      <div className="relative mx-auto rounded-sm overflow-hidden" style={{ width, height: h, backgroundColor: BANNER_BG }} data-testid="backdrop-canvas">
        {s > 0 && <span className="absolute border border-dashed border-amber-300/80 pointer-events-none" style={{ left: s * k, right: s * k, top: s * k, bottom: s * k }} data-testid="backdrop-safety" />}
        <span className={`absolute inset-x-0 top-1/2 border-t pointer-events-none ${snap.y ? "border-sky-300" : "border-white/10"}`} />
        <span className={`absolute inset-y-0 left-1/2 border-l pointer-events-none ${snap.x ? "border-sky-300" : "border-white/10"}`} />
        {b ? <div onPointerDown={drag("move")} className="absolute ring-1 ring-white/40 cursor-move" style={{ width: b.w * k, height: b.h * k, left: (b.cx - b.w / 2) * k, top: (b.cy - b.h / 2) * k, touchAction: "none" }} data-testid="backdrop-logo">
          <BannerLogo partner={d.partner} />
          <span onPointerDown={drag("size")} style={{ touchAction: "none" }} className="absolute -right-1.5 -bottom-1.5 h-3.5 w-3.5 rounded-sm bg-white border border-slate-500 cursor-nwse-resize" data-testid="backdrop-logo-resize" />
        </div> : <div className="absolute inset-0 grid place-items-center text-white/80 text-sm" data-testid="backdrop-drop">Logo-Datei (SVG, PDF, AI) hierher ziehen</div>}
      </div>
    </div>
  );
}

export function BackdropEditor({ customerId, spaceId, onChanged }) {
  const [d, setD] = useState(null);
  const [lay, setLay] = useState({ w_pct: 50, x_pct: 50, y_pct: 50 });
  const [busy, setBusy] = useState("");
  const file = useRef(null);
  const base = `/backdrop-print/${customerId}/${spaceId}`;
  const apply = (r) => { setD(r.data); setLay({ w_pct: r.data.w_pct, x_pct: r.data.x_pct, y_pct: r.data.y_pct }); };
  const load = () => api.get(base).then(apply).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  const [W, H] = d.final_mm || [1, 1];
  const save = (v) => api.put(base, v).then(apply).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  const b = d.aspect && d.final_mm ? logoBox(d, lay) : null;
  const setMm = (key, e) => {
    const v = num(e.target.value) * 10;
    if (!(v > 0) || !b) return;
    save({ ...lay, ...(key === "w" ? { w_pct: (v / W) * 100 } : { x_pct: ((v + b.w / 2) / W) * 100 }) });
  };
  const upload = async (f) => {
    if (!f) return;
    setBusy("upload");
    try { await uploadChunked(`/partner-graphics/${customerId}/banner-print`, f, () => {}); toast.success("Logo als Druckdatei im Profil gespeichert"); await load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload fehlgeschlagen"); } finally { setBusy(""); }
  };
  const saveSafety = (e) => {
    const v = num(e.target.value);
    if (!(v >= 0) || v * 10 === d.safety_mm) return;
    api.put("/backdrop-print/settings", { safety_mm: v * 10 }).then(load).then(() => toast.success("Sicherheitsabstand für alle Backdrops gespeichert"));
  };
  const dl = async (kind, name) => { setBusy(kind); try { await downloadBlob(`${base}/${kind}`, {}, name); } finally { setBusy(""); } };
  const errors = d.issues.filter((i) => i.level === "error");
  return (
    <div className="space-y-4 text-sm" data-testid="backdrop-editor">
      {d.final_mm ? <Canvas d={d} lay={lay} setLay={setLay} onDone={save} onFile={upload} /> : <p className="text-red-700">Im Leistungskatalog fehlen die Maße.</p>}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-slate-600">
        {b && <label className="flex items-center gap-1.5">Logobreite<Input key={`w${de(b.w)}`} defaultValue={de(b.w / 10)} onBlur={(e) => setMm("w", e)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="h-8 w-20 text-right" data-testid="backdrop-width-input" />cm <span className="text-xs text-slate-400">(Höhe {de(b.h / 10)} cm)</span></label>}
        {b && <label className="flex items-center gap-1.5">Abstand links<Input key={`x${de(b.cx)}`} defaultValue={de((b.cx - b.w / 2) / 10)} onBlur={(e) => setMm("x", e)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="h-8 w-20 text-right" data-testid="backdrop-left-input" />cm</label>}
        {b && <Button size="sm" variant="outline" onClick={() => save({ ...lay, x_pct: 50, y_pct: 50 })} data-testid="backdrop-center"><AlignCenterHorizontal size={14} className="mr-1.5" /> Mittig</Button>}
        <label className="flex items-center gap-1.5">Sicherheitsabstand (alle Backdrops)<Input key={`s${d.safety_mm}`} defaultValue={de(d.safety_mm / 10)} onBlur={saveSafety} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="h-8 w-16 text-right" data-testid="backdrop-safety-input" />cm</label>
        <input ref={file} type="file" accept=".svg,.pdf,.ai,.png,.webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="backdrop-file-input" />
        <Button size="sm" variant="outline" disabled={busy === "upload"} onClick={() => file.current?.click()} data-testid="backdrop-upload">{busy === "upload" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />} {d.partner.print ? "Logo ersetzen" : "Logo hochladen"}</Button>
      </div>
      <p className="text-xs text-slate-500">Logo mit der Maus verschieben, es rastet auf den Mittellinien ein (blaue Linie); Größe am Eckpunkt. Endformat {de(W / 10)} × {de(H / 10)} cm. Druck: 1:10, 5 mm Beschnitt, Logo 100 % K ohne Hintergrund, PDF/X-4 Fogra39.</p>
      {d.issues.length > 0 && <ul className="space-y-1" data-testid="backdrop-issues">{d.issues.map((i, n) => (
        <li key={n} className={`flex gap-2 ${i.level === "error" ? "text-red-700" : "text-amber-800"}`}>{i.level === "error" ? <XCircle size={15} className="mt-0.5 shrink-0" /> : <AlertTriangle size={15} className="mt-0.5 shrink-0" />}{i.msg}</li>
      ))}</ul>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" disabled={!b || !!busy} onClick={() => dl("approval", `Freigabe ${d.space.name} ${d.partner.name}.pdf`)} data-testid="backdrop-approval"><FileCheck2 size={14} className="mr-1.5" /> Freigabe-PDF für Kunden</Button>
        <Button disabled={!b || errors.length > 0 || !!busy} onClick={() => dl("pdf", `Druck ${d.space.name} ${d.partner.name}.pdf`)} className="bg-[#005448] hover:bg-[#00433a]" data-testid="backdrop-print-create"><Printer size={14} className="mr-1.5" /> Druck-PDF</Button>
      </div>
    </div>
  );
}

export function BackdropPrintDialog({ open, onClose, customerId, spaceId }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="backdrop-print-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Printer size={18} /> Backdrop-Gestaltung</DialogTitle></DialogHeader>
        <BackdropEditor customerId={customerId} spaceId={spaceId} />
      </DialogContent>
    </Dialog>
  );
}
